import { Button } from '../../../components/ui/button';
import { Upload, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { PostTemplate } from '../../post-template';
import { forwardRef, useState, useEffect } from 'react';
import { clampInstagramAspect } from '../../../shared/lib/instagramAspect';
import {
  type UserTag,
  sanitizeUsername,
  previewPointToMediaFraction,
  mediaFractionToPreviewPercent,
} from '../../../shared/lib/instagramTags';

type PreviewMode = 'simple' | 'template';

interface TemplateData {
  backgroundColor: string;
  useGradient?: boolean;
  backgroundColorEnd: string;
  backgroundAlpha: number;
  backgroundAlphaEnd: number;
  textColor: string;
  useTwoColors?: boolean;
  textColorSecondary?: string;
  fontFamily: string;
  fontUrl?: string;
  fontName?: string; // ← Добавляем fontName
  fontSize?: number; // ← Добавляем fontSize
  autoFontMax?: number;
  bandGrow?: boolean;
  logoUrl?: string;
  logoSize?: number;
  logoPosition?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'center';
  text?: string;
  textUppercase?: boolean;
  textAlign?: 'left' | 'center' | 'right';
  textLineSpacing?: number;
  fontBold?: boolean;
  showLine?: boolean;
  lineColor?: string;
  lineHeight?: number;
  linePosition?: 'top' | 'bottom';
  textShadowEnabled?: boolean;
  textShadowColor?: string;
  textShadowBlur?: number;
  textShadowWidth?: number;
  textSoftShadowEnabled?: boolean;
  textSoftShadowColor?: string;
  textSoftShadowBlur?: number;
  textSoftShadowOffsetX?: number;
  textSoftShadowOffsetY?: number;
  textSoftShadowOpacity?: number;
  textShadowOpacity?: number;
  postLayout?: 'bottom-bar' | 'top-card' | 'frame';
  accentColor?: string;
  cardColor?: string;
  cardInset?: number;
  bandHeight?: number;
  frameUrl?: string;
  frameTextX?: number;
  frameTextY?: number;
  frameTextWidth?: number;
}

interface MediaItem {
  url: string;
  type: 'image' | 'video';
}

interface PostPreviewProps {
  mode?: PreviewMode;
  imageUrl?: string;
  mediaItems?: MediaItem[]; // Для карусели
  mediaType?: 'image' | 'video'; // Тип медиа для одиночного режима
  onImageUpload?: (files: File[]) => void; // Изменили на массив File[]
  templateData?: TemplateData;
  aspectRatio?: string;
  showUploadButton?: boolean;
  applyTemplateToFirst?: boolean; // Применить шаблон только к первому элементу карусели
  currentSlide?: number; // Контролируемый текущий слайд
  onSlideChange?: (index: number) => void; // Колбэк при смене слайда
  fitMode?: 'fill' | 'fit'; // Режим отображения медиа
  userTags?: UserTag[];
  onUserTagsChange?: (next: UserTag[]) => void;
  /** Рендер сообщает подобранный кегль и потолок плашки, preview-space px. */
  onResolvedFontSize?: (info: { fontSize: number; max: number }) => void;
}

export const PostPreview = forwardRef<HTMLDivElement, PostPreviewProps>(({
  mode = 'simple',
  imageUrl,
  mediaItems,
  mediaType,
  onImageUpload,
  templateData,
  onResolvedFontSize,
  aspectRatio = 'square',
  showUploadButton = true,
  applyTemplateToFirst = false,
  currentSlide: controlledCurrentSlide,
  onSlideChange,
  fitMode = 'fill',
  userTags = [],
  onUserTagsChange,
}, ref) => {
  const [localCurrentSlide, setLocalCurrentSlide] = useState(0);
  const currentSlide = controlledCurrentSlide !== undefined ? controlledCurrentSlide : localCurrentSlide;

  // Натуральное соотношение сторон показываемого изображения (width/height).
  // Используем чтобы превью одиночного фото совпадало с тем, как его покажет Instagram.
  const [imgAspect, setImgAspect] = useState<number | null>(null);
  const [pendingTag, setPendingTag] = useState<{ x: number; y: number } | null>(null);
  const [tagDraft, setTagDraft] = useState('');
  const activeImageUrl = mediaItems && mediaItems.length > 0 ? mediaItems[currentSlide]?.url : imageUrl;
  useEffect(() => {
    setImgAspect(null);
  }, [activeImageUrl]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && onImageUpload) {
      onImageUpload(Array.from(files));
    }
  };

  const handleUploadClick = () => {
    document.getElementById('post-preview-upload')?.click();
  };

  const nextSlide = () => {
    if (mediaItems && currentSlide < mediaItems.length - 1) {
      if (onSlideChange) {
        onSlideChange(currentSlide + 1);
      } else {
        setLocalCurrentSlide(currentSlide + 1);
      }
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      if (onSlideChange) {
        onSlideChange(currentSlide - 1);
      } else {
        setLocalCurrentSlide(currentSlide - 1);
      }
    }
  };

  const goToSlide = (index: number) => {
    if (onSlideChange) {
      onSlideChange(index);
    } else {
      setLocalCurrentSlide(index);
    }
  };

  // Режим с шаблоном
  if (mode === 'template' && templateData) {
    return (
      <div className="space-y-4">
        <input
          id="post-preview-upload"
          type="file"
          accept="image/*,video/*"
          multiple
          onChange={handleFileChange}
          className="hidden"
        />
        
        <div className="w-full max-w-md mx-auto">
          <PostTemplate
            key={`${templateData.text}-${templateData.fontFamily}-${imageUrl}`}
            ref={ref}
            onResolvedFontSize={onResolvedFontSize}
            backgroundColor={templateData.backgroundColor}
            useGradient={templateData.useGradient}
            backgroundColorEnd={templateData.backgroundColorEnd}
            backgroundAlpha={templateData.backgroundAlpha}
            backgroundAlphaEnd={templateData.backgroundAlphaEnd}
            textColor={templateData.textColor}
            useTwoColors={templateData.useTwoColors}
            textColorSecondary={templateData.textColorSecondary}
            fontFamily={templateData.fontFamily}
            fontUrl={templateData.fontUrl}
            fontName={templateData.fontName} // ← Добавляем fontName
            fontSize={templateData.fontSize} // ← Добавляем fontSize
            logoUrl={templateData.logoUrl}
            logoSize={templateData.logoSize}
            logoPosition={templateData.logoPosition}
            imageUrl={imageUrl}
            text={templateData.text}
            textUppercase={templateData.textUppercase}
                  textAlign={templateData.textAlign}
            textLineSpacing={templateData.textLineSpacing}
            fontBold={templateData.fontBold}
            showLine={templateData.showLine}
            lineColor={templateData.lineColor}
            lineHeight={templateData.lineHeight}
            linePosition={templateData.linePosition}
            textShadowEnabled={templateData.textShadowEnabled}
            textShadowColor={templateData.textShadowColor}
            textShadowBlur={templateData.textShadowBlur}
            textShadowWidth={templateData.textShadowWidth}
            textSoftShadowEnabled={templateData.textSoftShadowEnabled}
            textSoftShadowColor={templateData.textSoftShadowColor}
            textSoftShadowBlur={templateData.textSoftShadowBlur}
            textSoftShadowOffsetX={templateData.textSoftShadowOffsetX}
            textSoftShadowOffsetY={templateData.textSoftShadowOffsetY}
            textSoftShadowOpacity={templateData.textSoftShadowOpacity}
            textShadowOpacity={templateData.textShadowOpacity}
            postLayout={templateData.postLayout}
            accentColor={templateData.accentColor}
            cardColor={templateData.cardColor}
            cardInset={templateData.cardInset}
            bandHeight={templateData.bandHeight}
            frameUrl={templateData.frameUrl}
            frameTextX={templateData.frameTextX}
            frameTextY={templateData.frameTextY}
            frameTextWidth={templateData.frameTextWidth}
            frameTextVAlign={templateData.frameTextVAlign}
            autoFontMax={templateData.autoFontMax}
            bandGrow={templateData.bandGrow}
            onImageClick={showUploadButton ? handleUploadClick : undefined}
            fitMode={fitMode}
            autoAspect
          />
        </div>
        
      </div>
    );
  }

  // Простой режим - одиночное фото/видео или карусель
  const isCarousel = mediaItems && mediaItems.length > 0;
  const currentMedia = isCarousel ? mediaItems[currentSlide] : null;
  const displayUrl = isCarousel ? currentMedia?.url : imageUrl;
  const mediaTypeForPreview = isCarousel ? currentMedia?.type : mediaType;

  // Проверяем нужно ли применить шаблон к текущему слайду
  const shouldApplyTemplate = applyTemplateToFirst && templateData && currentSlide === 0 && currentMedia?.type === 'image';

  // Numeric box aspect used for both the CSS style and tag coordinate mapping.
  // Single image: clamp to Instagram range; everything else: 4:5 (0.8).
  const singleImageBoxAspect: number =
    !isCarousel && mediaTypeForPreview === 'image' && imgAspect
      ? clampInstagramAspect(imgAspect)
      : 0.8;

  // Tagging is active only for simple single-image (no template applied) when a callback is provided.
  const taggingEnabled =
    !!onUserTagsChange &&
    !isCarousel &&
    mediaTypeForPreview === 'image' &&
    !shouldApplyTemplate;

  const handleBoxClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!taggingEnabled) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const fx = (e.clientX - rect.left) / rect.width;
    const fy = (e.clientY - rect.top) / rect.height;
    const frac = previewPointToMediaFraction(fx, fy, imgAspect || 1, singleImageBoxAspect);
    setPendingTag(frac);
    setTagDraft('');
  };

  const confirmTag = () => {
    const username = sanitizeUsername(tagDraft);
    if (username && pendingTag && onUserTagsChange) {
      onUserTagsChange([...userTags, { username, x: pendingTag.x, y: pendingTag.y }]);
    }
    setPendingTag(null);
    setTagDraft('');
  };

  const removeTag = (i: number) => {
    if (onUserTagsChange) {
      onUserTagsChange(userTags.filter((_, idx) => idx !== i));
    }
  };

  return (
    <div className="space-y-4">
      <input
        id="post-preview-upload"
        type="file"
        accept="image/*,video/*"
        multiple
        onChange={handleFileChange}
        className="hidden"
      />

      {taggingEnabled && (
        <p className="text-sm font-medium text-center">Отметить аккаунты — кликни по фото</p>
      )}

      <div
        className={`relative w-full max-w-md mx-auto rounded-lg overflow-hidden border group${taggingEnabled ? ' cursor-crosshair' : ''}`}
        style={{ aspectRatio: singleImageBoxAspect }}
        onClick={taggingEnabled ? handleBoxClick : undefined}
      >
        {/* Медиа контент */}
        {displayUrl ? (
          <>
            {shouldApplyTemplate ? (
              // Первый слайд с шаблоном
              <div className="w-full h-full [&>div]:w-full [&>div]:h-full [&>div]:border-0 [&>div]:rounded-none">
                <PostTemplate
                  key={`carousel-${templateData.text}-${templateData.fontFamily}-${displayUrl}`}
                  ref={ref}
                  onResolvedFontSize={onResolvedFontSize}
                  backgroundColor={templateData.backgroundColor}
                  useGradient={templateData.useGradient}
                  backgroundColorEnd={templateData.backgroundColorEnd}
                  backgroundAlpha={templateData.backgroundAlpha}
                  backgroundAlphaEnd={templateData.backgroundAlphaEnd}
                  textColor={templateData.textColor}
                  useTwoColors={templateData.useTwoColors}
                  textColorSecondary={templateData.textColorSecondary}
                  fontFamily={templateData.fontFamily}
                  fontUrl={templateData.fontUrl}
                  fontName={templateData.fontName} // ← Добавляем fontName
                  fontSize={templateData.fontSize} // ← Добавляем fontSize
                  logoUrl={templateData.logoUrl}
                  logoSize={templateData.logoSize}
                  logoPosition={templateData.logoPosition}
                  imageUrl={displayUrl}
                  text={templateData.text}
                  textUppercase={templateData.textUppercase}
                  textAlign={templateData.textAlign}
                  textLineSpacing={templateData.textLineSpacing}
                  fontBold={templateData.fontBold}
                  showLine={templateData.showLine}
                  lineColor={templateData.lineColor}
                  lineHeight={templateData.lineHeight}
                  linePosition={templateData.linePosition}
                  textShadowEnabled={templateData.textShadowEnabled}
                  textShadowColor={templateData.textShadowColor}
                  textShadowBlur={templateData.textShadowBlur}
                  textShadowWidth={templateData.textShadowWidth}
                  textSoftShadowEnabled={templateData.textSoftShadowEnabled}
                  textSoftShadowColor={templateData.textSoftShadowColor}
                  textSoftShadowBlur={templateData.textSoftShadowBlur}
                  textSoftShadowOffsetX={templateData.textSoftShadowOffsetX}
                  textSoftShadowOffsetY={templateData.textSoftShadowOffsetY}
                  textSoftShadowOpacity={templateData.textSoftShadowOpacity}
                  textShadowOpacity={templateData.textShadowOpacity}
                  postLayout={templateData.postLayout}
                  accentColor={templateData.accentColor}
                  cardColor={templateData.cardColor}
                  cardInset={templateData.cardInset}
                  bandHeight={templateData.bandHeight}
                  frameUrl={templateData.frameUrl}
                  frameTextX={templateData.frameTextX}
                  frameTextY={templateData.frameTextY}
                  frameTextWidth={templateData.frameTextWidth}
                  frameTextVAlign={templateData.frameTextVAlign}
                  autoFontMax={templateData.autoFontMax}
                  bandGrow={templateData.bandGrow}
                  onImageClick={undefined}
                  fitMode={fitMode}
                />
              </div>
            ) : mediaTypeForPreview === 'video' ? (
              <video
                src={displayUrl}
                className={`w-full h-full ${fitMode === 'fit' ? 'object-contain bg-black' : 'object-cover'}`}
                controls
                playsInline
              />
            ) : (
              <>
                {/* fit: поля закрываем размытой копией фото — 1:1 с рендером шаблона. */}
                {fitMode === 'fit' && (
                  <img
                    src={displayUrl}
                    alt=""
                    aria-hidden
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ filter: 'blur(20px)', transform: 'scale(1.1)' }}
                  />
                )}
                <img
                  src={displayUrl}
                  alt="Preview"
                  className={`relative z-10 w-full h-full ${fitMode === 'fit' ? 'object-contain' : 'object-cover'}${!taggingEnabled ? ' cursor-pointer hover:opacity-90 transition-opacity' : ''}`}
                  onClick={!taggingEnabled && showUploadButton && !isCarousel ? handleUploadClick : undefined}
                  onLoad={(e) => {
                    const { naturalWidth, naturalHeight } = e.currentTarget;
                    if (naturalWidth > 0 && naturalHeight > 0) {
                      setImgAspect(naturalWidth / naturalHeight);
                    }
                  }}
                />
              </>
            )}

            {/* Tag markers — only in simple single-image tagging mode */}
            {taggingEnabled && userTags.map((t, i) => {
              const { left, top } = mediaFractionToPreviewPercent(t.x, t.y, imgAspect || 1, singleImageBoxAspect);
              return (
                <button
                  key={`${t.username}-${t.x.toFixed(4)}-${t.y.toFixed(4)}`}
                  type="button"
                  onClick={(e) => { e.stopPropagation(); removeTag(i); }}
                  className="absolute -translate-x-1/2 -translate-y-1/2 bg-black/70 text-white text-xs rounded-full px-2 py-1 flex items-center gap-1"
                  style={{ left: `${left}%`, top: `${top}%` }}
                >
                  @{t.username}
                  <X className="h-3 w-3" />
                </button>
              );
            })}

            {/* Навигация карусели */}
            {isCarousel && mediaItems.length > 1 && (
              <>
                {/* Кнопки навигации */}
                {currentSlide > 0 && (
                  <button
                    onClick={prevSlide}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                )}

                {currentSlide < mediaItems.length - 1 && (
                  <button
                    onClick={nextSlide}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                )}
              </>
            )}
          </>
        ) : (
          <div
            className="w-full h-full flex items-center justify-center bg-muted cursor-pointer hover:opacity-90 transition-opacity"
            onClick={handleUploadClick}
          >
            <div className="text-center">
              <Upload className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                Нажмите для загрузки {isCarousel ? 'медиа' : 'изображения'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Username input for pending tag */}
      {taggingEnabled && pendingTag && (
        <div className="flex gap-2">
          <input
            autoFocus
            value={tagDraft}
            placeholder="username"
            onChange={(e) => setTagDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                confirmTag();
              } else if (e.key === 'Escape') {
                setPendingTag(null);
                setTagDraft('');
              }
            }}
            className="flex-1 border rounded px-2 py-1 text-sm"
          />
          <button type="button" onClick={confirmTag} className="text-sm px-3 border rounded">
            ОК
          </button>
        </div>
      )}
    </div>
  );
});

PostPreview.displayName = 'PostPreview';